import { tokenizeWords } from './alignment-utils';

// Block-level tags that create visual separation between adjacent text nodes.
// When two text nodes are in different block-level elements we insert a virtual
// space so tokens at the boundary don't get incorrectly merged (e.g. the last
// word of one paragraph and the first word of the next).
// Inline containers (strong, em, a, code, span, …) are intentionally excluded so
// that cross-node tokens like "ה-<strong>Max</strong>" stay as one token.
const BLOCK_TAGS = new Set([
  'P',
  'H1',
  'H2',
  'H3',
  'H4',
  'H5',
  'H6',
  'LI',
  'BLOCKQUOTE',
  'DIV',
  'SECTION',
]);

function nearestBlockAncestor(node: Text, root: HTMLElement): Element | null {
  let cur: Node | null = node.parentNode;
  while (cur && cur !== root) {
    if (cur.nodeType === Node.ELEMENT_NODE && BLOCK_TAGS.has((cur as Element).tagName)) {
      return cur as Element;
    }
    cur = cur.parentNode;
  }
  return null;
}

// TreeWalker filter — skips text inside <pre>, code-header labels, and any
// element marked data-tts-ignore (activity trail, tool UIs, carousels, …).
function ttsNodeFilter(node: Node): number {
  if (node.nodeType !== Node.TEXT_NODE) {
    const el = node as Element;
    if (
      el.tagName === 'PRE' ||
      el.tagName === 'TABLE' ||
      el.classList?.contains('aui-code-header-root') ||
      el.hasAttribute?.('data-tts-ignore')
    ) {
      return NodeFilter.FILTER_REJECT;
    }
    return NodeFilter.FILTER_SKIP;
  }
  return NodeFilter.FILTER_ACCEPT;
}

// A position in the flat string that maps back to a real DOM text node + offset.
// Null entries are virtual whitespace separators inserted between block boundaries —
// they are never the start or end of a matched token.
type NodePos = { node: Text; offset: number } | null;

function buildFlatText(el: HTMLElement): { flat: string; map: NodePos[] } {
  const chars: string[] = [];
  const map: NodePos[] = [];
  let lastBlock: Element | null | undefined = undefined;

  const walker = document.createTreeWalker(el, NodeFilter.SHOW_ALL, ttsNodeFilter);
  while (walker.nextNode()) {
    const node = walker.currentNode;
    if (node.nodeType !== Node.TEXT_NODE) continue;

    const block = nearestBlockAncestor(node as Text, el);
    // Insert a virtual space when crossing a block boundary so adjacent paragraphs
    // don't run their last/first words together into a single token.
    if (lastBlock !== undefined && block !== lastBlock) {
      chars.push(' ');
      map.push(null);
    }
    lastBlock = block;

    const text = (node as Text).textContent ?? '';
    for (let i = 0; i < text.length; i++) {
      chars.push(text[i]);
      map.push({ node: node as Text, offset: i });
    }
  }

  return { flat: chars.join(''), map };
}

export interface HighlightWordInDomParams {
  /** Container whose text nodes are searched. */
  el: HTMLElement;
  /** Word to match (case-insensitive, edge punctuation stripped). */
  word: string;
  /** Which occurrence of `word` to highlight (0-based, global across the container). */
  targetOccurrence: number;
}

/**
 * Walks all text nodes inside `el`, finds the `targetOccurrence`-th occurrence
 * of `word` (0-based, case-insensitive, same whitespace tokenisation as
 * `buildWordSegments`), and paints a positioned overlay highlight. Supports
 * cross-node tokens like "ה-<strong>Max</strong>" where the word spans
 * multiple DOM text nodes.
 *
 * Returns a cleanup function that fades and removes the overlay, or
 * `undefined` if the word/occurrence wasn't found in the DOM.
 */
export function highlightWordInDom({
  el,
  word,
  targetOccurrence,
}: HighlightWordInDomParams): (() => void) | undefined {
  const wordLower = word.toLowerCase();
  const { flat, map } = buildFlatText(el);

  let occCount = 0;
  let matchStart = -1;
  let matchEnd = -1;

  // Same tokeniser as buildWordSegments so occurrence indices stay in sync.
  for (const token of tokenizeWords(flat)) {
    if (token.word.toLowerCase() !== wordLower) continue;
    if (occCount === targetOccurrence) {
      matchStart = token.start;
      matchEnd = token.start + token.word.length;
      break;
    }
    occCount++;
  }

  if (matchStart === -1 || matchEnd > map.length || matchStart >= map.length) return undefined;

  const startPos = map[matchStart];
  const endPos = map[matchEnd - 1];
  if (!startPos || !endPos) return undefined;

  const matchRange = document.createRange();
  matchRange.setStart(startPos.node, startPos.offset);
  matchRange.setEnd(endPos.node, endPos.offset + 1);

  const wordRect = matchRange.getBoundingClientRect();
  const containerRect = el.getBoundingClientRect();

  const overlay = document.createElement('div');
  overlay.style.cssText = [
    'position:absolute',
    'pointer-events:none',
    'border-radius:3px',
    'z-index:-1',
    `background-color:color-mix(in srgb, var(--brand-accent) 25%, transparent)`,
    `top:${wordRect.top - containerRect.top}px`,
    `left:${wordRect.left - containerRect.left}px`,
    `width:${wordRect.width}px`,
    `height:${wordRect.height}px`,
  ].join(';');

  el.appendChild(overlay);

  return () => {
    overlay.style.transition = 'opacity 100ms ease-in';
    overlay.style.opacity = '0';
    const remove = () => overlay.isConnected && overlay.remove();
    overlay.addEventListener('transitionend', remove, { once: true });
    setTimeout(remove, 200);
  };
}
